import Cleave from 'cleave.js/react.js';
import { capitalize } from 'lodash-es';
import { DateTime } from 'luxon';
import React from 'react';

import { IBaseProps } from '../../utils';

import { Error } from '../../Error';
import { Icon } from '../../Icon';

import { getContainerClasses } from '../BaseInput/BaseInput';
import { ValidationStates } from '../enums';

import styles from '../BaseInput/BaseInput.module.scss';

export interface IMonthInputProps extends IBaseProps {
  /** Id attribute assigned to the input field. Used to associate the input with other form elements (Ex: label) */
  id?: string;
  size?: 'default' | 'small' | 'large';
  /** The current value of the input that is displayed to the user */
  value?: string;
  /** Validation state of the input which can be either success or error */
  validationState?: ValidationStates;
  /** Set to true if you want to disable the input from accepting changes from the user */
  disabled?: boolean;
  /** Determines if field is required */
  required?: boolean;
  /** Application level error message that the component will display regardless of internal error state */
  errorMessage?: string;
  /** This callback is executed anytime validation happens in the month input component.
   * Returns true or false for isMonthValid, the current date being validated,
   *  and whether or not the date is complete
   */
  onMonthValid?(isMonthValid: boolean, date: string, isCompleteDate: boolean): void;
  /** Application level error message that the component will display regardless of internal error state */
  onChange?(newValue: string): void;
}

export class MonthInput extends React.Component<IMonthInputProps, any> {
  static defaultProps = {
    size: 'default',
    onChange: () => {},
    onMonthValid: () => {}
  };

  state = {
    validationState: ValidationStates.None,
    errorMessage: null
  };

  handleOnBlur = (event) => {
    const validDateState = {
      validationState: ValidationStates.Success,
      errorMessage: ''
    };
    const invalidDateState = {
      validationState: ValidationStates.Error,
      errorMessage: 'The month specified is invalid. Enter a month as MM/YYYY.'
    };
    const requiredDateState = {
      validationState: ValidationStates.Error,
      errorMessage: 'A month is required. Enter a month as MM/YYYY.'
    };
    const value = event.target.value;
    const date = DateTime.fromFormat(value, 'mm/yyyy');
    let state = null;
    if (!value && this.props.required) {
      state = requiredDateState;
      this.props.onMonthValid(false, value, false);
    } else if (date.isValid) {
      state = validDateState;
      this.props.onMonthValid(true, value, true);
    } else {
      state = invalidDateState;
      this.props.onMonthValid(false, value, false);
    }
    this.setState(state);
  };

  handleOnChange = (event) => {
    const value = event.target.value;
    this.props.onChange(value);
  };

  render() {
    const inputSize = capitalize(this.props.size);
    const validationState = this.props.validationState || this.state.validationState;

    const containerClasses = getContainerClasses(inputSize, validationState, this.props.disabled);

    return (
      <>
        <div className={containerClasses}>
          <Cleave
            id={this.props.id}
            className={styles.input}
            disabled={this.props.disabled}
            options={{ date: true, datePattern: ['m', 'Y'], delimiter: '/' }}
            value={this.props.value}
            placeholder="MM/YYYY"
            onBlur={this.handleOnBlur}
            onChange={this.handleOnChange}
            {...(this.props.dataTest && { 'data-test': this.props.dataTest })}
          />
          {validationState && (
            <span className={styles.iconWrapper}>
              {validationState === ValidationStates.Success && <Icon className={styles.icon} icon="status-success" />}
              {validationState === ValidationStates.Error && <Icon className={styles.icon} icon="status-error" />}
            </span>
          )}
        </div>
        {this.state.errorMessage && <Error>{this.state.errorMessage}</Error>}
      </>
    );
  }
}
